import { checkIsBusinessUser } from "selectors/user";
import { getActiveOrganization } from "selectors/organizations";
import { getCurrentXeroOrganizationId, getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useSelector } from "react-redux";
import Countries from "const/Countries";
import DocumentPaymentTypes from "const/DocumentPaymentTypes";
import FieldContainer from "../FieldContainer";
import Objects from "utils/Objects";
import React, { useCallback, useMemo } from "react";
import SelectContactInput from "nlib/common/SelectContactInput";
import useDocumentContext from "hooks/useDocumentContext";

const AddressField = (props) => {
  const id = "address";

  const { uiTexts, messages } = useSelector(getTextsData);

  const { countryCode } = useSelector(getActiveOrganization);

  const {
    extraData: { integrationService } = {},
    settings: { allowClientPayeeSelection } = {}
  } = useSelector(getSelectedBusinessData);

  const businessUser = useSelector(checkIsBusinessUser);

  const xeroBusiness = !!useSelector(getCurrentXeroOrganizationId);

  const {
    documentState: { [id]: value, vendorId, type, paymentType, currency, address: prevAddress },
    documentFrozen,
    recogniseData,
    setDocumentState
  } = useDocumentContext();

  const useAbn = (countryCode === Countries.AU || countryCode === Countries.NZ) && paymentType === DocumentPaymentTypes.BUY;

  const hasAddressName = !!(value?.name || "").trim();

  const buyPaymentType = paymentType === DocumentPaymentTypes.BUY;

  const active = !documentFrozen && !!hasAddressName && !vendorId;

  const confidenceFields = useMemo(() => {
    const path = `addresses.${buyPaymentType ? "sender" : "recipient"}`;

    const nameChanged = value?.name !== Objects.getPropertyByPath(recogniseData, `${path}.name`);

    return paymentType && !nameChanged && [`${path}.name`];
  }, [buyPaymentType, paymentType, recogniseData, value?.name]);

  const label = useMemo(() => {
    const first = !xeroBusiness && paymentType ? (buyPaymentType ? uiTexts.vendor : uiTexts.customer) : uiTexts.businessName;

    return `${first}${active ? ` (${uiTexts.notLinked})` : ""}`;
  }, [xeroBusiness, active, buyPaymentType, paymentType, uiTexts]);

  const handleChange = useCallback((newValue) => {
    if (useAbn && (newValue.name !== prevAddress?.name)) {
      setDocumentState({ [id]: newValue, vatId: newValue.vatId || "" });
    } else {
      setDocumentState({ [id]: newValue });
    }
  }, [useAbn, prevAddress?.name, setDocumentState]);

  return (
    <FieldContainer
      useWidget
      withButton
      portal
      id={id}
      disabled={businessUser && !allowClientPayeeSelection}
      vendorId={vendorId}
      label={label}
      placeholder={(!xeroBusiness && (!paymentType || !type)) ? messages.selectDocumentType : uiTexts.enterContactName}
      confidenceFields={confidenceFields}
      invalid={!vendorId}
      valid={!!vendorId}
      active={active}
      value={value}
      currency={currency}
      displayValue={value?.name}
      probablyVendor={buyPaymentType}
      onlyVendors={paymentType && buyPaymentType && integrationService && !xeroBusiness}
      onlyCustomers={paymentType && !buyPaymentType && integrationService && !xeroBusiness}
      onChange={(!xeroBusiness && (!paymentType || !type)) ? undefined : handleChange}
      component={SelectContactInput}
      {...props} />
  );
};

export default React.memo(AddressField);
